import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * ProductWordmark — three presentations of the "StyxDesk" product name,
 * laid out for an enterprise SaaS feel. The Styxtech corporate logo
 * (rendered by <BrandMark>) lives separately; this component is purely
 * about making the *product name* legible and confident.
 *
 * Pick which one ships site-wide via the SHIPPED_VARIATION constant in
 * PRODUCT_WORDMARK_VARIATIONS below — every consumer (sidebar, login,
 * page headers) reads the same key, so changing it is a single-line PR.
 *
 * VARIATIONS
 * ──────────
 *
 * 1. "icon-tile" — Bold "ST" tile + "StyxDesk" wordmark.
 *    Closest to ServiceNow / Atlassian / Linear: a coloured square monogram
 *    next to the product name in a heavy weight. Reads as a tier-1 SaaS
 *    badge. Best when there's NO Styxtech corporate logo nearby — the tile
 *    IS the brand mark. Use this if you ever drop the corporate wordmark
 *    from the sidebar.
 *
 * 2. "underline" — "StyxDesk" wordmark in heavy display weight, with an
 *    indigo→cyan accent bar underneath. Quietly confident. Pairs cleanly
 *    with the corporate logo because there's no second tile competing for
 *    attention. This is the default ship.
 *
 * 3. "pill" — Slim outlined pill: tiny ST monogram + "StyxDesk" lockup.
 *    Reads like a product badge inside a wider corporate brand bar.
 *    Atlassian "Jira", Microsoft "Teams", Salesforce "Sales Cloud" all
 *    do this. Best inside an existing branded shell.
 *
 * Each variation has THREE sizes:
 *   - 'sm' for sidebar / app-shell header
 *   - 'md' for page headers / cards
 *   - 'lg' for login splash / marketing pages
 */
import { useTheme } from '@/store/theme';
/**
 * Single source of truth for the chosen variation across the whole app.
 * Change this one line to switch every surface (sidebar, login, etc.).
 *
 *   - 'icon-tile' → bold ST tile + name
 *   - 'underline' → wordmark with accent underline      ← SHIPPED
 *   - 'pill'      → outlined pill badge
 *
 * Decision (2026-05-07): the underline pairs cleanly with the Styxtech
 * corporate logo in the sidebar/login splash without doubling up monograms.
 * Tier-1 hierarchy: corporate brand carries identity, the underline accent
 * gives StyxDesk its own unmistakable mark.
 */
export const SHIPPED_VARIATION = 'underline';
export function ProductWordmark({ variant = SHIPPED_VARIATION, size = 'md', className, }) {
    switch (variant) {
        case 'icon-tile': return _jsx(IconTile, { size: size, className: className });
        case 'underline': return _jsx(UnderlineWM, { size: size, className: className });
        case 'pill': return _jsx(PillBadge, { size: size, className: className });
    }
}
// ───────────────────────── Variation 1: icon-tile ─────────────────────────
function IconTile({ size, className }) {
    // Tile / wordmark dimensions per size. We size the tile to roughly match
    // the cap-height of the wordmark so the lockup reads as one unit.
    const tile = size === 'lg' ? 'h-11 w-11 text-base' : size === 'md' ? 'h-9 w-9 text-sm' : 'h-7 w-7 text-[11px]';
    const word = size === 'lg' ? 'text-2xl' : size === 'md' ? 'text-lg' : 'text-sm';
    return (_jsxs("div", { className: ['inline-flex items-center gap-2.5', className ?? ''].join(' '), children: [_jsx("span", { className: [
                    'inline-flex items-center justify-center rounded-lg font-extrabold tracking-tight text-white',
                    'bg-gradient-to-br from-brand-600 to-violet-600 shadow-brand',
                    tile,
                ].join(' '), "aria-hidden": "true", children: "ST" }), _jsx("span", { className: [
                    'font-extrabold tracking-tight text-slate-900 dark:text-slate-50',
                    word,
                ].join(' '), children: "StyxDesk" })] }));
}
// ───────────────────────── Variation 2: underline ─────────────────────────
function UnderlineWM({ size, className }) {
    const word = size === 'lg' ? 'text-3xl' : size === 'md' ? 'text-xl' : 'text-base';
    const bar = size === 'lg' ? 'h-[3px] w-12' : size === 'md' ? 'h-[2.5px] w-8' : 'h-[2px] w-6';
    return (_jsxs("div", { className: ['inline-flex flex-col items-start', className ?? ''].join(' '), children: [_jsxs("span", { className: [
                    'font-extrabold tracking-tight text-slate-900 dark:text-slate-50',
                    word,
                ].join(' '), children: ["Styx", _jsx("span", { className: "text-brand-600 dark:text-brand-400", children: "Desk" })] }), _jsx("span", { className: [
                    'mt-1 rounded-full bg-gradient-to-r from-brand-500 to-cyan-400',
                    bar,
                ].join(' '), "aria-hidden": "true" })] }));
}
// ───────────────────────── Variation 3: pill ─────────────────────────
function PillBadge({ size, className }) {
    const { resolvedTheme } = useTheme();
    const padding = size === 'lg' ? 'px-3 py-1.5' : size === 'md' ? 'px-2.5 py-1' : 'px-2 py-0.5';
    const word = size === 'lg' ? 'text-base' : size === 'md' ? 'text-sm' : 'text-xs';
    const tile = size === 'lg' ? 'h-5 w-5 text-[10px]' : size === 'md' ? 'h-4 w-4 text-[9px]' : 'h-3.5 w-3.5 text-[8px]';
    // Subtle outlined pill so it sits unobtrusively next to the corporate logo.
    // Matches Atlassian "/Jira", Salesforce "/Sales Cloud" etc.
    const tone = resolvedTheme === 'dark'
        ? 'border-slate-700 bg-slate-900/60 text-slate-100'
        : 'border-slate-300 bg-white/80 text-slate-800';
    return (_jsxs("span", { className: [
            'inline-flex items-center gap-1.5 rounded-full border backdrop-blur-sm',
            padding, tone, className ?? '',
        ].join(' '), children: [_jsx("span", { className: [
                    'inline-flex items-center justify-center rounded-sm font-bold text-white',
                    'bg-gradient-to-br from-brand-600 to-violet-600',
                    tile,
                ].join(' '), "aria-hidden": "true", children: "ST" }), _jsx("span", { className: ['font-semibold tracking-tight', word].join(' '), children: "StyxDesk" })] }));
}
